import React, {FunctionComponent, SVGProps} from 'react';
import styled, {CSSProperties} from 'styled-components';
import {typographyStyle} from '@/components/Typography';
import {Flexbox, FlexCenter} from '@/styled-components';
import {interceptProps} from '@/styled-components/utils';
import {palette} from '@/styles';

export type variant = 'fill' | 'outline' | 'ghost';
export type color =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'black'
  | 'red'
  | 'blue'
  | 'linkPrimary'
  | 'white'
  | 'teal'; // (컴포넌트 가이드에는 없는데 지도에서 많이 쓰여서 임시로 추가함)
export type size =
  | 'large' // 50
  | 'mediumLarge' // 48
  | 'medium' // 42
  | 'regular' //32
  | 'small'; //24

export interface ButtonProps extends React.ComponentPropsWithoutRef<any> {
  variant?: variant;
  color?: color;
  size?: size;
  fullWidth?: boolean;
  width?: number | string;
  noPadding?: boolean;
}

const SIZE_STYLE = ({size}: ButtonProps) =>
  ({
    large: {
      ...typographyStyle.medium16,
      padding: `14px 15px`,
      borderRadius: 10,
    },
    mediumLarge: {
      ...typographyStyle.bold14,
      padding: `14px 15px`,
      borderRadius: 5,
    },
    medium: {
      ...typographyStyle.medium14,
      padding: `11px 15px`,
    },
    regular: {
      ...typographyStyle.medium14,
      padding: '6px 11px',
    },
    small: {
      ...typographyStyle.medium12,
      padding: '4px 7px',
    },
  }[size || 'medium']);

const VARIANT_STYLE = ({variant}: ButtonProps) =>
  ({
    fill: {
      color: palette.white,
      border: `solid 1px transparent`,
    },
    outline: {
      backgroundColor: palette.white,
      border: `solid 1px ${palette.gray200}`,
    },
    ghost: {},
  }[variant || 'fill']);

const COLOR_STYLE = ({color}: ButtonProps) =>
  ({
    black: {
      color: palette.gray800,
    },
    blue: {
      color: palette.blue100,
    },
    linkPrimary: {
      color: palette.linkPrimary,
    },
    red: {
      color: palette.red100,
    },
    teal: {
      color: palette.teal100,
    },
    primary: {
      backgroundColor: palette.teal200,
      borderColor: palette.teal200,
    },
    secondary: {
      backgroundColor: '#3E4650',
      borderColor: '#3E4650',
    },
    tertiary: {
      color: palette.teal200,
      backgroundColor: '#E3F6F8',
      borderColor: '#E4E6E7',
    },
    white: {
      color: palette.gray800,
      backgroundColor: '#FFFFFF',
      borderColor: palette.gray200,
    },
  }[color || 'white']);

const HOVER_STYLE = ({color}: {color: string}) =>
  ({
    primary: {
      backgroundColor: palette.teal100,
      borderColor: palette.teal100,
    },
    secondary: {
      backgroundColor: '#5B5D5F',
      borderColor: '#5B5D5F',
    },
    tertiary: {
      borderColor: '#E3F6F8',
    },
  }[color || 'white']);

const FOCUS_STYLE = ({color}: {color: string}) =>
  ({
    primary: {
      backgroundColor: '#025C61',
      borderColor: '#025C61',
    },
    secondary: {
      backgroundColor: '#2D343C',
      borderColor: '#2D343C',
    },
    tertiary: {
      borderColor: palette.teal100,
    },
  }[color || 'white']);

export const Button = styled('button').withConfig({
  shouldForwardProp: interceptProps([
    'variant',
    'color',
    'size',
    'fullWidth',
    'width',
    'icon',
    'iconSize',
    'noPadding',
  ]),
})<ButtonProps>`
  cursor: pointer;
  border: none;
  font-weight: 700;
  border-radius: 5px;
  ${VARIANT_STYLE}
  ${SIZE_STYLE}
  ${COLOR_STYLE}
  &:hover(:not(:disabled)) {
    ${HOVER_STYLE as any}
  }
  &:focus(:not(:disabled)) {
    outline: none;
    ${FOCUS_STYLE as any}
  }
  &:disabled {
    cursor: default;
    opacity: 0.3;
  }
  ${({variant}) =>
    variant === 'outline' &&
    `
    &:hover(:not(:disabled)), &:focus(:not(:disabled)) {
      border-color: ${palette.gray300};
    }
  `}
  ${({fullWidth, width}) =>
    !!(fullWidth || width) &&
    `
    width: 100%;
    padding-left: 0;
    padding-right: 0;
    max-width: ${
      fullWidth ? '100%' : typeof width === 'number' ? `${width}px` : width
    };
  `}
  ${({noPadding}) => noPadding && `padding: 0;`}
`;

export const ButtonGroup = styled(Flexbox).withConfig({
  shouldForwardProp: interceptProps(['spacing', 'flex']),
})<{
  spacing?: number;
  flex?: number | string;
}>`
  ${Button} {
    flex-shrink: 0;
    ${({flex}) => flex && `flex: ${flex};`}
  }
  gap: ${({spacing = 1}) => spacing * 4}px;
`;

interface IconButtonProps extends ButtonProps {
  icon: FunctionComponent<SVGProps<SVGSVGElement>> | string; // 아이콘 이미지 url
  iconSize?: number | [number, number]; // width, height
  reverse?: boolean;
  gap?: CSSProperties['gap'];
}

export const IconButton = ({
  icon,
  iconSize = 16,
  color,
  variant,
  children,
  reverse,
  gap,
  ...props
}: IconButtonProps) => {
  const iconColor =
    COLOR_STYLE({color})?.color ||
    VARIANT_STYLE({variant})?.color ||
    palette.textPrimary;

  return (
    <Button {...props} color={color} variant={variant}>
      <FlexCenter
        gap={gap ?? '4px'}
        style={reverse ? {flexDirection: 'row-reverse'} : undefined}
      >
        {typeof icon === 'string' ? (
          <ButtonIcon as="img" src={icon} size={iconSize} alt="button-icon" />
        ) : (
          <ButtonIcon as={icon} color={iconColor} size={iconSize} />
        )}
        {children}
      </FlexCenter>
    </Button>
  );
};

const ButtonIcon = styled('div')<{
  size: IconButtonProps['iconSize'];
}>`
  ${({size}) =>
    size &&
    (Array.isArray(size)
      ? `width: ${size[0]}px; height: ${size[1]}px;`
      : `width: ${size}px; height: ${size}px;`)}
`;
